import React from "react";
import { motion } from "framer-motion";

const CurrentDiagnosis = ({
  category,
  formData,
  errors,
  touched,
  // personalCancerHistory,
  oncologyDiagnosisHistory,
  hereditaryDiagnosisHistory,
  familyCancerHistory,
  familyDiseaseHistory,
  handleChange,
  handleBlur,
  handleDateChange,
  handleDateBlur,
  handleOncologyDiagnosisChange,
  handleHereditaryDiagnosisChange,
  // handlePersonalCancerChange,
  handleFamilyCancerChange,
  handleFamilyDiseaseChange,
  // addPersonalCancerEntry,
  addOncologyDiagnosisEntry,
  // removePersonalCancerEntry,
  removeOncologyDiagnosisEntry,
  addHereditaryDiagnosisEntry,
  removeHereditaryDiagnosisEntry,
  addFamilyCancerEntry,
  removeFamilyCancerEntry,
  addFamilyDiseaseEntry,
  removeFamilyDiseaseEntry,
  updateForm,
  setTouched,
}) => {
  const cancerTypeSpecs = {
    "solid tumor": [
      "Hereditary Breast Cancer",
      "Ovarian Cancer",
      "Prostate Cancer",
      "Endometrial (Uterine) Cancer",
      "Colorectal (Bowel) Cancer (including Lynch Syndrome and Familial adenomatous polyposis (FAP))",
      "Gastric (Stomach) Cancer",
      "Pancreatic Cancer",
      "Liver Cancer",
      "Skin Cancers",
      "Melanoma",
      "Basal Cell Carcinoma",
      "Kidney (Renal) Cancer",
      "Bladder Cancer",
      "Brain Tumors (e.g. astrocytoma, glioblastoma, oligodendroglioma and ependymoma)",
      "Neurofibromatosis-associated tumors",
      "Thyroid Cancer",
      "Lung Cancer",
      "Sarcomas (soft tissue and bone)",
      "Eye Cancer (Retinoblastoma)",
    ],
    "blood cancer": [
      "Leukemia (familial forms of acute myeloid leukemia (AML), acute lymphoblastic leukemia (ALL), chronic lymphocytic leukemia (CLL))",
      "Lung Cancer",
      "Multiple Myeloma",
      "Myelodysplastic Syndromes (MDS)",
      "Bone Marrow Failure Syndromes",
    ],
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const diseaseCategorySpecs = {
    Cardiovascular: [
      "Heart disease (early heart attacks, arrhythmias, cardiomyopathy)",
      "Hypertension (high blood pressure)",
      "Stroke",
    ],
    "Metabolic & Endocrine": [
      "Diabetes (Type 1 and Type 2)",
      "Thyroid disorders",
    ],
    Cancers: [
      "Breast cancer (especially BRCA1/BRCA2 mutations)",
      "Ovarian cancer",
      "Colon cancer (familial adenomatous polyposis, Lynch syndrome)",
      "Prostate cancer",
      "Other cancers with known familial clustering",
    ],
    "Neurological & Psychiatric": [
      "Alzheimer’s disease / dementia",
      "Parkinson’s disease",
      "Epilepsy",
      "Autism spectrum disorders",
      "Schizophrenia, bipolar disorder, major depression",
    ],
    "Blood & Immune": [
      "Sickle cell disease",
      "Thalassemia",
      "Hemophilia",
      "Autoimmune diseases (e.g., lupus, rheumatoid arthritis, multiple sclerosis)",
    ],
    Respiratory: [
      "Asthma",
      "Cystic fibrosis",
    ],
    "Other Genetic Disorders": [
      "Kidney disease (e.g., polycystic kidney disease)",
      "Muscular dystrophy",
      "Huntington’s disease",
      "Marfan syndrome",
      "Certain congenital heart defects",
    ],
  };

  const handleTreatmentChange = (e) => {
    const { name, checked } = e.target;
    let updatedTreatments = Array.isArray(formData.treatments)
      ? [...formData.treatments]
      : [];

    if (name === "none of the above") {
      if (checked) {
        updatedTreatments = ["none of the above"];
      }
    } else {
      if (checked) {
        updatedTreatments = updatedTreatments.filter(
          (t) => t !== "none of the above"
        );
        updatedTreatments.push(name);
      } else {
        updatedTreatments = updatedTreatments.filter((t) => t !== name);
      }
    }

    updateForm("treatments", updatedTreatments);
  };

  return (
    <motion.div variants={itemVariants} className="form-group">
      <h3>Current diagnosis and patient history</h3>
      {category === "oncology" && (
        <>
          <div className="form-group">
            <label>Diagnosis</label>
            {oncologyDiagnosisHistory.map((entry, index) => (
              <div key={index} className="repeater-group">
                <div className="repeater-inputs">
                  <div className="repeater-input">
                    <label>Cancer type</label>
                    <select
                      name={`cancerType_${index}`}
                      value={entry.cancerType}
                      onChange={(e) =>
                        handleOncologyDiagnosisChange(index, "cancerType", e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`cancerType_${index}`]: true,
                        }))
                      }
                    >
                      <option value="">Select cancer type</option>
                      <option value="solid tumor">Solid Tumor</option>
                      <option value="blood cancer">Blood Cancer</option>
                    </select>
                    {errors[`cancerType_${index}`] && (
                      <span className="error">{errors[`cancerType_${index}`]}</span>
                    )}
                  </div>
                  <div className="repeater-input">
                    <label>Specification</label>
                    <select
                      name={`cancerTypeSpec_${index}`}
                      value={entry.cancerTypeSpec}
                      onChange={(e) =>
                        handleOncologyDiagnosisChange(
                          index,
                          "cancerTypeSpec",
                          e.target.value
                        )
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`cancerTypeSpec_${index}`]: true,
                        }))
                      }
                      disabled={!entry.cancerType}
                    >
                      {entry.cancerType ? (
                        <>
                          <option value="">Select specification</option>
                          {cancerTypeSpecs[entry.cancerType].map((spec, i) => (
                            <option key={i} value={spec}>
                              {spec}
                            </option>
                          ))}
                        </>
                      ) : (
                        <option value="">Fill cancer type first</option>
                      )}
                    </select>
                    {errors[`cancerTypeSpec_${index}`] && (
                      <span className="error">
                        {errors[`cancerTypeSpec_${index}`]}
                      </span>
                    )}
                  </div>
                  <div className="repeater-input">
                    <label>At age</label>
                    <input
                      type="text"
                      name={`age_${index}`}
                      value={entry.age}
                      onChange={(e) => {
                        const value = e.target.value.slice(0, 2);
                        if (
                          value === "" ||
                          (Number(value) >= 0 &&
                            Number(value) <= 99 &&
                            !isNaN(Number(value)))
                        ) {
                          handleOncologyDiagnosisChange(index, "age", value);
                        }
                      }}
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`age_${index}`]: true,
                        }))
                      }
                      placeholder="Enter age"
                      maxLength="2"
                      style={{
                        WebkitAppearance: "none",
                        MozAppearance: "textfield",
                      }}
                    />
                    {errors[`age_${index}`] && (
                      <span className="error">{errors[`age_${index}`]}</span>
                    )}
                  </div>
                </div>
                {index > 0 && (
                  <button
                    type="button"
                    className="delete-button"
                    onClick={() => removeOncologyDiagnosisEntry(index)}
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="add-more-button"
              onClick={addOncologyDiagnosisEntry}
            >
              Add secondary
            </button>
          </div>
          <div className="form-group">
            <label>Stage</label>
            <select
              name="stage"
              value={formData.stage}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select from dropdown</option>
              <option value="Stage 0 - Carcinoma in situ">
                Stage 0 - Carcinoma in situ
              </option>
              <option value="Stage I - Localized Cancer">
                Stage I - Localized Cancer
              </option>
              <option value="Stage II - Early Local Spread">
                Stage II - Early Local Spread
              </option>
              <option value="Stage III - Regional Spread">
                Stage III - Regional Spread
              </option>
              <option value="Stage IV - Distant Spread (Metastatic)">
                Stage IV - Distant Spread (Metastatic)
              </option>
            </select>
            {errors.stage && <span className="error">{errors.stage}</span>}
          </div>
          <div className="form-group">
            <label>
              Disease at the time of testing
            </label>
            <select
              name="diseaseAtTime"
              value={formData.diseaseAtTime}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select from dropdown</option>
              <option value="metastatic">Metastatic</option>
              <option value="recurrent">Recurrent</option>
              <option value="relapse">Relapse</option>
              <option value="none of the above">None of the above</option>
            </select>
            {errors.diseaseAtTime && (
              <span className="error">{errors.diseaseAtTime}</span>
            )}
          </div>
          <div className="form-group dob">
            <label>Date of original diagnosis</label>
            <div className="dob-inputs">
              <div className="dob-input">
                <select
                  name="month"
                  value={formData.dateOfDiagnosis.month}
                  onChange={(e) => handleDateChange("month", e.target.value)}
                  onBlur={() => handleDateBlur("month")}
                >
                  <option value="">Month</option>
                  {Array.from({ length: 12 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {new Date(0, i).toLocaleString("default", {
                        month: "long",
                      })}
                    </option>
                  ))}
                </select>
                {errors.month && <span className="error">{errors.month}</span>}
              </div>
              <div className="dob-input">
                <input
                  type="text"
                  name="date"
                  value={formData.dateOfDiagnosis.date}
                  onChange={(e) => handleDateChange("date", e.target.value)}
                  onBlur={() => handleDateBlur("date")}
                  placeholder="Day"
                  maxLength="2"
                  style={{
                    WebkitAppearance: "none",
                    MozAppearance: "textfield",
                  }}
                />
                {errors.date && <span className="error">{errors.date}</span>}
              </div>
              <div className="dob-input">
                <input
                  type="text"
                  name="year"
                  value={formData.dateOfDiagnosis.year}
                  onChange={(e) => handleDateChange("year", e.target.value)}
                  onBlur={() => handleDateBlur("year")}
                  placeholder="Year"
                  maxLength="4"
                  style={{
                    WebkitAppearance: "none",
                    MozAppearance: "textfield",
                  }}
                />
                {errors.year && <span className="error">{errors.year}</span>}
              </div>
            </div>
          </div>
          <div className="form-group prior-form-group">
            <label>Prior or current treatments (select all that apply)</label>
            <div className="checkbox-group treatment-checkboxes">
              {[
                "targeted therapy",
                "immunotherapy",
                "chemotherapy",
                "combo therapy",
                "none of the above",
              ].map((option, index) => (
                <div key={index} className="checkbox-option">
                  <input
                    type="checkbox"
                    name={option}
                    checked={formData.treatments.includes(option)}
                    onChange={handleTreatmentChange}
                    onBlur={handleBlur}
                    className="treatment-checkbox"
                  />
                  <label>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </label>
                </div>
              ))}
            </div>
            {errors.treatments && (
              <span className="error">{errors.treatments}</span>
            )}
          </div>
          <div className="form-group">
            <label>Has the patient ever received a transplant?</label>
            <select
              name="transplant"
              value={formData.transplant}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select from dropdown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {errors.transplant && (
              <span className="error">{errors.transplant}</span>
            )}
          </div>
        </>
      )}
      {category === "hereditary_cancer" && (
        <>
          {/* <div className="form-group">
            <label>Primary tumor</label>
            {personalCancerHistory.map((entry, index) => (
              <div key={index} className="repeater-group">
                <div className="repeater-inputs">
                  <div className="repeater-input">
                    <label>Cancer type</label>
                    <select
                      name={`cancerType_${index}`}
                      value={entry.cancerType}
                      onChange={(e) =>
                        handlePersonalCancerChange(
                          index,
                          "cancerType",
                          e.target.value
                        )
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`cancerType_${index}`]: true,
                        }))
                      }
                    >
                      <option value="">Select cancer type</option>
                      <option value="solid tumor">Solid Tumor</option>
                      <option value="blood cancer">Blood Cancer</option>
                    </select>
                    {errors[`cancerType_${index}`] && (
                      <span className="error">
                        {errors[`cancerType_${index}`]}
                      </span>
                    )}
                  </div>
                  <div className="repeater-input">
                    <label>Specification</label>
                    <select
                      name={`cancerTypeSpec_${index}`}
                      value={entry.cancerTypeSpec}
                      onChange={(e) =>
                        handlePersonalCancerChange(
                          index,
                          "cancerTypeSpec",
                          e.target.value
                        )
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`cancerTypeSpec_${index}`]: true,
                        }))
                      }
                      disabled={!entry.cancerType}
                    >
                      {entry.cancerType ? (
                        <>
                          <option value="">Select specification</option>
                          {cancerTypeSpecs[entry.cancerType].map((spec, i) => (
                            <option key={i} value={spec}>
                              {spec}
                            </option>
                          ))}
                        </>
                      ) : (
                        <option value="">Fill cancer type first</option>
                      )}
                    </select>
                    {errors[`cancerTypeSpec_${index}`] && (
                      <span className="error">
                        {errors[`cancerTypeSpec_${index}`]}
                      </span>
                    )}
                  </div>
                  <div className="repeater-input">
                    <label>At age</label>
                    <input
                      type="text"
                      name={`age_${index}`}
                      value={entry.age}
                      onChange={(e) => {
                        const value = e.target.value.slice(0, 2);
                        if (
                          value === "" ||
                          (Number(value) >= 0 &&
                            Number(value) <= 99 &&
                            !isNaN(Number(value)))
                        ) {
                          handlePersonalCancerChange(index, "age", value);
                        }
                      }}
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`age_${index}`]: true,
                        }))
                      }
                      placeholder="Enter age"
                      maxLength="2"
                      style={{
                        WebkitAppearance: "none",
                        MozAppearance: "textfield",
                      }}
                    />
                    {errors[`age_${index}`] && (
                      <span className="error">{errors[`age_${index}`]}</span>
                    )}
                  </div>
                </div>
                {index > 0 && (
                  <button
                    type="button"
                    className="delete-button"
                    onClick={() => removePersonalCancerEntry(index)}
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="add-more-button"
              onClick={addPersonalCancerEntry}
            >
              Add secondary tumor
            </button>
          </div> */}
          
          <div className="form-group">
            <label>
              Does the patient have a current diagnosis of hematological cancer?
            </label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="hematologicalCancer"
                  value="yes"
                  checked={formData.hematologicalCancer === "yes"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="hematologicalCancer"
                  value="no"
                  checked={formData.hematologicalCancer === "no"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                No
              </label>
            </div>
            {errors.hematologicalCancer && (
              <span className="error">{errors.hematologicalCancer}</span>
            )}
          </div>

          {/* Show diagnosis repeater and 2 questions only if hematological cancer is "yes" */}
          {formData.hematologicalCancer === "yes" && (
            <>
              {/* Diagnosis Repeater Group */}
              <div className="form-group">
                <label>Diagnosis</label>
                {hereditaryDiagnosisHistory.map((entry, index) => (
                  <div key={index} className="repeater-group">
                    <div className="repeater-inputs">
                      <div className="repeater-input">
                        <label>Cancer type</label>
                        <select
                          name={`hereditary_cancerType_${index}`}
                          value={entry.cancerType}
                          onChange={(e) =>
                            handleHereditaryDiagnosisChange(index, "cancerType", e.target.value)
                          }
                          onBlur={() =>
                            setTouched((prev) => ({
                              ...prev,
                              [`hereditary_cancerType_${index}`]: true,
                            }))
                          }
                        >
                          <option value="">Select cancer type</option>
                          <option value="solid tumor">Solid Tumor</option>
                          <option value="blood cancer">Blood Cancer</option>
                        </select>
                        {errors[`hereditary_cancerType_${index}`] && (
                          <span className="error">{errors[`hereditary_cancerType_${index}`]}</span>
                        )}
                      </div>
                      <div className="repeater-input">
                        <label>Specification</label>
                        <select
                          name={`hereditary_cancerTypeSpec_${index}`}
                          value={entry.cancerTypeSpec}
                          onChange={(e) =>
                            handleHereditaryDiagnosisChange(
                              index,
                              "cancerTypeSpec",
                              e.target.value
                            )
                          }
                          onBlur={() =>
                            setTouched((prev) => ({
                              ...prev,
                              [`hereditary_cancerTypeSpec_${index}`]: true,
                            }))
                          }
                          disabled={!entry.cancerType}
                        >
                          {entry.cancerType ? (
                            <>
                              <option value="">Select specification</option>
                              {cancerTypeSpecs[entry.cancerType].map((spec, i) => (
                                <option key={i} value={spec}>
                                  {spec}
                                </option>
                              ))}
                            </>
                          ) : (
                            <option value="">Fill cancer type first</option>
                          )}
                        </select>
                        {errors[`hereditary_cancerTypeSpec_${index}`] && (
                          <span className="error">
                            {errors[`hereditary_cancerTypeSpec_${index}`]}
                          </span>
                        )}
                      </div>
                      <div className="repeater-input">
                        <label>At age</label>
                        <input
                          type="text"
                          name={`hereditary_age_${index}`}
                          value={entry.age}
                          onChange={(e) => {
                            const value = e.target.value.slice(0, 2);
                            if (
                              value === "" ||
                              (Number(value) >= 0 &&
                                Number(value) <= 99 &&
                                !isNaN(Number(value)))
                            ) {
                              handleHereditaryDiagnosisChange(index, "age", value);
                            }
                          }}
                          onBlur={() =>
                            setTouched((prev) => ({
                              ...prev,
                              [`hereditary_age_${index}`]: true,
                            }))
                          }
                          placeholder="Enter age"
                          maxLength="2"
                          style={{
                            WebkitAppearance: "none",
                            MozAppearance: "textfield",
                          }}
                        />
                        {errors[`hereditary_age_${index}`] && (
                          <span className="error">{errors[`hereditary_age_${index}`]}</span>
                        )}
                      </div>
                    </div>
                    {index > 0 && (
                      <button
                        type="button"
                        className="delete-button"
                        onClick={() => removeHereditaryDiagnosisEntry(index)}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="add-more-button"
                  onClick={addHereditaryDiagnosisEntry}
                >
                  Add secondary
                </button>
              </div>

              <div className="form-group">
                <label>Is the patient currently receiving radiation therapy?</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="radiationTherapy"
                      value="yes"
                      checked={formData.radiationTherapy === "yes"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="radiationTherapy"
                      value="no"
                      checked={formData.radiationTherapy === "no"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    No
                  </label>
                </div>
                {errors.radiationTherapy && (
                  <span className="error">{errors.radiationTherapy}</span>
                )}
              </div>
              <div className="form-group">
                <label>Is the patient a bone marrow transplant recipient?</label>
                <div className="radio-group">
                  <label>
                    <input
                      type="radio"
                      name="boneMarrowTransplant"
                      value="yes"
                      checked={formData.boneMarrowTransplant === "yes"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    Yes
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="boneMarrowTransplant"
                      value="no"
                      checked={formData.boneMarrowTransplant === "no"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    No
                  </label>
                </div>
                {errors.boneMarrowTransplant && (
                  <span className="error">{errors.boneMarrowTransplant}</span>
                )}
              </div>
            </>
          )}
          
          {/* Genetic mutations question - always visible, separate from conditional logic */}
          <div className="form-group">
            <label>Is there a known family history of genetic mutations?</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  name="geneticMutations"
                  value="yes"
                  checked={formData.geneticMutations === "yes"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  name="geneticMutations"
                  value="no"
                  checked={formData.geneticMutations === "no"}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                No
              </label>
            </div>
            {errors.geneticMutations && (
              <span className="error">{errors.geneticMutations}</span>
            )}
            {formData.geneticMutations === "yes" && (
              <div className="form-group specify-family-group">
                <label>Specify family member</label>
                <select
                  name="geneticMutationFamilyMember"
                  value={formData.geneticMutationFamilyMember || ""}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  <option value="">Select family member</option>
                  <option value="father">Father</option>
                  <option value="mother">Mother</option>
                  <option value="son">Son</option>
                  <option value="daughter">Daughter</option>
                  <option value="brother">Brother</option>
                  <option value="sister">Sister</option>
                  <option value="uncle">Uncle</option>
                  <option value="aunt">Aunt</option>
                  <option value="cousin">Cousin</option>
                  <option value="nephew">Nephew</option>
                  <option value="niece">Niece</option>
                  <option value="grandfather">Grandfather</option>
                  <option value="grandmother">Grandmother</option>
                </select>
                {errors.geneticMutationFamilyMember && (
                  <span className="error">
                    {errors.geneticMutationFamilyMember}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="form-group">
            <label>Does the patient have a family history of cancer?</label>
            <select
              name="familyHistoryCancer"
              value={formData.familyHistoryCancer}
              onChange={handleChange}
              onBlur={handleBlur}
            >
              <option value="">Select from dropdown</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
            {errors.familyHistoryCancer && (
              <span className="error">{errors.familyHistoryCancer}</span>
            )}
          </div>
          {formData.familyHistoryCancer === "yes" && (
            <div className="form-group">
              <label>Family Cancer History</label>
              {familyCancerHistory.map((entry, index) => (
                <div key={index} className="repeater-group">
                  <div className="repeater-inputs">
                    <div className="repeater-input">
                      <label>Relationship</label>
                      <select
                        name={`relationship_${index}`}
                        value={entry.relationship}
                        onChange={(e) =>
                          handleFamilyCancerChange(
                            index,
                            "relationship",
                            e.target.value
                          )
                        }
                        onBlur={() =>
                          setTouched((prev) => ({
                            ...prev,
                            [`relationship_${index}`]: true,
                          }))
                        }
                      >
                        <option value="">Select relationship</option>
                        <option value="father">Father</option>
                        <option value="mother">Mother</option>
                        <option value="son">Son</option>
                        <option value="daughter">Daughter</option>
                        <option value="brother">Brother</option>
                        <option value="sister">Sister</option>
                        <option value="uncle">Uncle</option>
                        <option value="aunt">Aunt</option>
                        <option value="cousin">Cousin</option>
                        <option value="nephew">Nephew</option>
                        <option value="niece">Niece</option>
                        <option value="grandfather">Grandfather</option>
                        <option value="grandmother">Grandmother</option>
                      </select>
                      {errors[`relationship_${index}`] && (
                        <span className="error">
                          {errors[`relationship_${index}`]}
                        </span>
                      )}
                    </div>
                    <div className="repeater-input">
                      <label>Cancer type</label>
                      <select
                        name={`familyCancerType_${index}`}
                        value={entry.cancerType}
                        onChange={(e) =>
                          handleFamilyCancerChange(
                            index,
                            "cancerType",
                            e.target.value
                          )
                        }
                        onBlur={() =>
                          setTouched((prev) => ({
                            ...prev,
                            [`familyCancerType_${index}`]: true,
                          }))
                        }
                      >
                        <option value="">Select cancer type</option>
                        <option value="solid tumor">Solid Tumor</option>
                        <option value="blood cancer">Blood Cancer</option>
                      </select>
                      {errors[`familyCancerType_${index}`] && (
                        <span className="error">
                          {errors[`familyCancerType_${index}`]}
                        </span>
                      )}
                    </div>
                    <div className="repeater-input">
                      <label>Specification</label>
                      <select
                        name={`familyCancerTypeSpec_${index}`}
                        value={entry.cancerTypeSpec || ""}
                        onChange={(e) =>
                          handleFamilyCancerChange(
                            index,
                            "cancerTypeSpec",
                            e.target.value
                          )
                        }
                        onBlur={() =>
                          setTouched((prev) => ({
                            ...prev,
                            [`familyCancerTypeSpec_${index}`]: true,
                          }))
                        }
                        disabled={!entry.cancerType}
                      >
                        {entry.cancerType ? (
                          <>
                            <option value="">Select specification</option>
                            {cancerTypeSpecs[entry.cancerType].map(
                              (spec, i) => (
                                <option key={i} value={spec}>
                                  {spec}
                                </option>
                              )
                            )}
                          </>
                        ) : (
                          <option value="">Fill cancer type first</option>
                        )}
                      </select>
                      {errors[`familyCancerTypeSpec_${index}`] && (
                        <span className="error">
                          {errors[`familyCancerTypeSpec_${index}`]}
                        </span>
                      )}
                    </div>
                    <div className="repeater-input">
                      <label>Age Range</label>
                      <select
                        name={`ageRange_${index}`}
                        value={entry.ageRange}
                        onChange={(e) =>
                          handleFamilyCancerChange(
                            index,
                            "ageRange",
                            e.target.value
                          )
                        }
                        onBlur={() =>
                          setTouched((prev) => ({
                            ...prev,
                            [`ageRange_${index}`]: true,
                          }))
                        }
                      >
                        <option value="">Select age at diagnosis</option>
                        <option value="Below 20 years">Below 20 years</option>
                        <option value="20 - 30 years">20 - 30 years</option>
                        <option value="30 - 40 years">30 - 40 years</option>
                        <option value="40 - 50 years">40 - 50 years</option>
                        <option value="50 - 60 years">50 - 60 years</option>
                        <option value="Above 60 years">Above 60 years</option>
                        <option value="I don't know">I don't know</option>
                      </select>
                      {errors[`ageRange_${index}`] && (
                        <span className="error">
                          {errors[`ageRange_${index}`]}
                        </span>
                      )}
                    </div>
                    <div className="repeater-input">
                      <label>Maternal/Paternal</label>
                      <select
                        name={`maternalPaternal_${index}`}
                        value={entry.maternalPaternal}
                        onChange={(e) =>
                          handleFamilyCancerChange(
                            index,
                            "maternalPaternal",
                            e.target.value
                          )
                        }
                        onBlur={() =>
                          setTouched((prev) => ({
                            ...prev,
                            [`maternalPaternal_${index}`]: true,
                          }))
                        }
                      >
                        <option value="">Select maternal/paternal</option>
                        <option value="maternal">Maternal</option>
                        <option value="paternal">Paternal</option>
                      </select>
                      {errors[`maternalPaternal_${index}`] && (
                        <span className="error">
                          {errors[`maternalPaternal_${index}`]}
                        </span>
                      )}
                    </div>
                    <div className="repeater-input">
                      <label>Relative available for testing?</label>
                      <select
                        name={`relativeAvailable_${index}`}
                        value={entry.relativeAvailable}
                        onChange={(e) =>
                          handleFamilyCancerChange(
                            index,
                            "relativeAvailable",
                            e.target.value
                          )
                        }
                        onBlur={() =>
                          setTouched((prev) => ({
                            ...prev,
                            [`relativeAvailable_${index}`]: true,
                          }))
                        }
                      >
                        <option value="">Select availability</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                      {errors[`relativeAvailable_${index}`] && (
                        <span className="error">
                          {errors[`relativeAvailable_${index}`]}
                        </span>
                      )}
                    </div>
                  </div>
                  {index > 0 && (
                    <button
                      type="button"
                      className="delete-button"
                      onClick={() => removeFamilyCancerEntry(index)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="add-more-button"
                onClick={addFamilyCancerEntry}
              >
                Add more
              </button>
            </div>
          )}
        </>
      )}
      {category === "hereditary_diseases" && (
        <>
          <div className="form-group">
            <label>Family history of disease</label>
            {familyDiseaseHistory.map((entry, index) => (
              <div key={index} className="repeater-group">
                <div className="repeater-inputs">
                  <div className="repeater-input">
                    <label>Relationship</label>
                    <select
                      name={`diseaseRelationship_${index}`}
                      value={entry.relationship}
                      onChange={(e) =>
                        handleFamilyDiseaseChange(index, "relationship", e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`diseaseRelationship_${index}`]: true,
                        }))
                      }
                    >
                      <option value="">Select relationship</option>
                      <option value="father">Father</option>
                      <option value="mother">Mother</option>
                      <option value="son">Son</option>
                      <option value="daughter">Daughter</option>
                      <option value="brother">Brother</option>
                      <option value="sister">Sister</option>
                      <option value="uncle">Uncle</option>
                      <option value="aunt">Aunt</option>
                      <option value="cousin">Cousin</option>
                      <option value="nephew">Nephew</option>
                      <option value="niece">Niece</option>
                      <option value="grandfather">Grandfather</option>
                      <option value="grandmother">Grandmother</option>
                    </select>
                    {errors[`diseaseRelationship_${index}`] && (
                      <span className="error">{errors[`diseaseRelationship_${index}`]}</span>
                    )}
                  </div>
                  <div className="repeater-input">
                    <label>Disease category</label>
                    <select
                      name={`diseaseCategory_${index}`}
                      value={entry.diseaseCategory}
                      onChange={(e) =>
                        handleFamilyDiseaseChange(index, "diseaseCategory", e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`diseaseCategory_${index}`]: true,
                        }))
                      }
                    >
                      <option value="">Select disease category</option>
                      {Object.keys(diseaseCategorySpecs).map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    {errors[`diseaseCategory_${index}`] && (
                      <span className="error">{errors[`diseaseCategory_${index}`]}</span>
                    )}
                  </div>
                  <div className="repeater-input">
                    <label>Specification</label>
                    <select
                      name={`diseaseSpecification_${index}`}
                      value={entry.diseaseSpecification}
                      onChange={(e) =>
                        handleFamilyDiseaseChange(index, "diseaseSpecification", e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`diseaseSpecification_${index}`]: true,
                        }))
                      }
                      disabled={!entry.diseaseCategory}
                    >
                      {entry.diseaseCategory ? (
                        <>
                          <option value="">Select specification</option>
                          {diseaseCategorySpecs[entry.diseaseCategory].map((spec, i) => (
                            <option key={i} value={spec}>{spec}</option>
                          ))}
                        </>
                      ) : (
                        <option value="">Fill disease category first</option>
                      )}
                    </select>
                    {errors[`diseaseSpecification_${index}`] && (
                      <span className="error">{errors[`diseaseSpecification_${index}`]}</span>
                    )}
                  </div>
                  <div className="repeater-input">
                    <label>Age Range</label>
                    <select
                      name={`diseaseAgeRange_${index}`}
                      value={entry.ageRange}
                      onChange={(e) =>
                        handleFamilyDiseaseChange(index, "ageRange", e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`diseaseAgeRange_${index}`]: true,
                        }))
                      }
                    >
                      <option value="">Select age at diagnosis</option>
                      <option value="Below 20 years">Below 20 years</option>
                      <option value="20 - 30 years">20 - 30 years</option>
                      <option value="30 - 40 years">30 - 40 years</option>
                      <option value="40 - 50 years">40 - 50 years</option>
                      <option value="50 - 60 years">50 - 60 years</option>
                      <option value="Above 60 years">Above 60 years</option>
                      <option value="I don't know">I don't know</option>
                    </select>
                    {errors[`diseaseAgeRange_${index}`] && (
                      <span className="error">{errors[`diseaseAgeRange_${index}`]}</span>
                    )}
                  </div>
                  <div className="repeater-input">
                    <label>Maternal/Paternal</label>
                    <select
                      name={`diseaseMaternalPaternal_${index}`}
                      value={entry.maternalPaternal}
                      onChange={(e) =>
                        handleFamilyDiseaseChange(index, "maternalPaternal", e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`diseaseMaternalPaternal_${index}`]: true,
                        }))
                      }
                    >
                      <option value="">Select maternal/paternal</option>
                      <option value="maternal">Maternal</option>
                      <option value="paternal">Paternal</option>
                    </select>
                    {errors[`diseaseMaternalPaternal_${index}`] && (
                      <span className="error">{errors[`diseaseMaternalPaternal_${index}`]}</span>
                    )}
                  </div>
                  <div className="repeater-input">
                    <label>Relative available for testing?</label>
                    <select
                      name={`diseaseRelativeAvailable_${index}`}
                      value={entry.relativeAvailable}
                      onChange={(e) =>
                        handleFamilyDiseaseChange(index, "relativeAvailable", e.target.value)
                      }
                      onBlur={() =>
                        setTouched((prev) => ({
                          ...prev,
                          [`diseaseRelativeAvailable_${index}`]: true,
                        }))
                      }
                    >
                      <option value="">Select availability</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                    {errors[`diseaseRelativeAvailable_${index}`] && (
                      <span className="error">{errors[`diseaseRelativeAvailable_${index}`]}</span>
                    )}
                  </div>
                </div>
                {index > 0 && (
                  <button
                    type="button"
                    className="delete-button"
                    onClick={() => removeFamilyDiseaseEntry(index)}
                  >
                    Delete
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="add-more-button"
              onClick={addFamilyDiseaseEntry}
            >
              Add more
            </button>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default CurrentDiagnosis;
